import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import api, { SESSION_EXPIRED_EVENT, SESSION_EXPIRED_MESSAGE } from '../api/api';
import { MOCK_MENU, MOCK_ORDER_HISTORY } from '../utils/mockData';
import { findMenuItem as _findMenuItemHelper, toLocalISODate } from '../utils/helpers';

const AppContext = createContext();
export function useApp() {
  return useContext(AppContext);
}

function parseStoredJson(value) {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch (e) {
    console.error('Failed to parse stored JSON:', e);
    return null;
  }
}

// normalize incoming item (API or mock) to a common shape used by cart/totals
function normalizeItem(raw, userDivision, isTakeaway = false, userType = null, useSelectedAddressDivision = false) {
  if (!raw) return null;
  const id = raw.ItemId ?? raw.id ?? raw.Item_No ?? raw.ItemNo ?? String(raw.ItemId ?? raw.id ?? raw.Item_No ?? raw.ItemNo ?? '');
  const name = raw.Item_Desc1 ?? raw.name ?? raw.ItemName ?? 'Unnamed Item';

  // Calculate regular price based on user type and division
  // NEW USERS always get Circle3Rate regardless of their division
  let regularPrice = 0;
  if (userType === 'New User' && !useSelectedAddressDivision) {
    // New customers always get Circle3 rate
    regularPrice = Number(raw.Circle3Rate ?? raw.MRP ?? raw.price ?? 0) || 0;
  } else if (userDivision === 'Circle1') {
    regularPrice = Number(raw.Circle1Rate ?? raw.MRP ?? raw.price ?? 0) || 0;
  } else if (userDivision === 'Circle2') {
    regularPrice = Number(raw.Circle2Rate ?? raw.MRP ?? raw.price ?? 0) || 0;
  } else if (userDivision === 'Circle3') {
    regularPrice = Number(raw.Circle3Rate ?? raw.MRP ?? raw.price ?? 0) || 0;
  } else {
    regularPrice = Number(raw.Circle3Rate ?? raw.Circle2Rate ?? raw.Circle1Rate ?? raw.MRP ?? raw.price ?? 0) || 0;
  }

  // Get takeaway price if available
  const takeawayPrice = raw.TakeAway !== undefined ? Number(raw.TakeAway) : regularPrice;

  // Use appropriate price based on isTakeaway state
  const price = isTakeaway && raw.TakeAway !== undefined ? takeawayPrice : regularPrice;

  console.log('normalizeItem: userDivision =', userDivision, 'userType =', userType, 'isTakeaway =', isTakeaway, 'regularPrice =', regularPrice, 'takeawayPrice =', takeawayPrice, 'finalPrice =', price, 'for item', name);

  const image = raw.image ?? raw.Image ?? name;
  const type = (raw.Item_Type ?? raw.type ?? '').toLowerCase();
  return { id: String(id), name, price, image, type, raw, regularPrice, takeawayPrice, isTakeaway };
}

export function AppProvider({ children }) {
  // Initialize cart from sessionStorage
  const [cart, setCart] = useState(() => {
    const saved = sessionStorage.getItem('cart');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved cart:', e);
      }
    }
    return {};
  });

  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [menuItems, setMenuItems] = useState(MOCK_MENU);
  const [orderDetails, setOrderDetails] = useState(JSON.parse(localStorage.getItem('orderDetails')) || null);
  const [orderHistory, setOrderHistory] = useState(JSON.parse(localStorage.getItem('orderHistory')) || MOCK_ORDER_HISTORY);
  const [deliveryMode, setDeliveryMode] = useState(() => {
    const savedMode = sessionStorage.getItem('deliveryMode');
    return savedMode === 'takeaway' || savedMode === 'delivery' ? savedMode : null;
  });
  const [isTakeaway, setIsTakeawayState] = useState(() => {
    const savedMode = sessionStorage.getItem('deliveryMode');
    return savedMode === 'takeaway';
  });

  // Local runtime mapping to resolve API items by id (keeps item metadata for cart/totals)
  const [menuItemMap, setMenuItemMap] = useState(() => {
    const saved = sessionStorage.getItem('menuItemMap');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved menuItemMap:', e);
      }
    }
    return window.__menuItemMap ? { ...window.__menuItemMap } : {};
  });

  useEffect(() => {
    const savedUser =
      sessionStorage.getItem('user') ||
      localStorage.getItem('user') ||
      localStorage.getItem('tempUser');
    const authFlag = sessionStorage.getItem('isAuthenticated');
    const token = api.getAccessToken();
    const storedVid = localStorage.getItem('Vid');
    const savedOrder = sessionStorage.getItem('orderDetails');

    if (savedUser && (authFlag === 'true' || token)) {
      const parsedUser = parseStoredJson(savedUser);
      if (parsedUser) {
        setUser(parsedUser);
        setIsAuthenticated(true);
        sessionStorage.setItem('user', JSON.stringify(parsedUser));
        sessionStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify(parsedUser));
      }
    } else if (token && storedVid) {
      const restoredUser = {
        Vid: Number(storedVid),
        UserType: 'Existing User'
      };

      setUser(restoredUser);
      setIsAuthenticated(true);
      sessionStorage.setItem('user', JSON.stringify(restoredUser));
      sessionStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('user', JSON.stringify(restoredUser));
    }

    if (savedOrder) {
      const parsedOrder = parseStoredJson(savedOrder);
      if (parsedOrder) setOrderDetails(parsedOrder);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.Vid || user.UserType !== 'New User') return;

    let isMounted = true;

    const checkAndUpdateActiveCustomer = async () => {
      try {
        const res = await api.GetIndividualCustomerList(user.Vid, 'Existing User');
        const customer =
          res?.data?.GetIndividualCustomerListjs?.Table?.[0] ||
          res?.GetIndividualCustomerListjs?.Table?.[0] ||
          null;

        const isActive = Number(customer?.IsActive) === 1;

        if (!isMounted || !isActive) return;

        const updatedUser = { ...user, UserType: 'Existing User' };
        setUser(updatedUser);
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch (err) {
        console.error('Failed to check inactive customer status:', err);
      }
    };

    checkAndUpdateActiveCustomer();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user]);

  useEffect(() => {
    sessionStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (orderDetails) sessionStorage.setItem('orderDetails', JSON.stringify(orderDetails));
    else sessionStorage.removeItem('orderDetails');
  }, [orderDetails]);

  useEffect(() => {
    sessionStorage.setItem('isAuthenticated', isAuthenticated);
    if (user) {
      const serializedUser = JSON.stringify(user);
      sessionStorage.setItem('user', serializedUser);
      localStorage.setItem('user', serializedUser);
    } else {
      sessionStorage.removeItem('user');
    }
  }, [isAuthenticated, user]);

  // Keep a global map so other modules (helpers.findMenuItem) can fallback to it
  useEffect(() => {
    window.__menuItemMap = { ...(window.__menuItemMap || {}), ...menuItemMap };
  }, [menuItemMap]);

  // Persist menuItemMap to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('menuItemMap', JSON.stringify(menuItemMap));
  }, [menuItemMap]);

  useEffect(() => {
    if (!deliveryMode) {
      sessionStorage.removeItem('deliveryMode');
      return;
    }
    sessionStorage.setItem('deliveryMode', deliveryMode);
  }, [deliveryMode]);

  const setDeliveryModeAndPricing = (mode) => {
    const normalizedMode = mode === 'takeaway' ? 'takeaway' : 'delivery';
    setDeliveryMode(normalizedMode);
    setIsTakeawayState(normalizedMode === 'takeaway');
  };

  const setIsTakeaway = (valueOrUpdater) => {
    setIsTakeawayState((prev) => {
      const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : !!valueOrUpdater;
      setDeliveryMode(next ? 'takeaway' : 'delivery');
      return next;
    });
  };

  // find item by id using several strategies: runtime map -> mock helper -> menuItems array/object
  const findMenuItem = (itemId) => {
    if (itemId === undefined || itemId === null) return null;
    const key = String(itemId);

    console.log('findMenuItem: looking for itemId =', key, 'isTakeaway =', isTakeaway);

    // 1) runtime map (populated when items are added from API)
    if (menuItemMap && menuItemMap[key]) {
      const cachedItem = menuItemMap[key];
      console.log('findMenuItem: found in menuItemMap =', cachedItem);

      if (cachedItem.raw) {
        return normalizeItem(
          cachedItem.raw,
          user?.division,
          isTakeaway,
          user?.UserType,
          !!user?.selectedAdditionalAddress
        );
      }
      
      // Return item with price based on current isTakeaway state
      // Use regular/takeaway prices when available, fallback to legacy cached "price"
      const regularPrice = cachedItem.regularPrice ?? cachedItem.price ?? 0;
      const takeawayPrice = cachedItem.takeawayPrice ?? cachedItem.price ?? regularPrice;
      const price = isTakeaway ? takeawayPrice : regularPrice;
      
      return { ...cachedItem, regularPrice, takeawayPrice, price };
    }
    if (typeof window !== 'undefined' && window.__menuItemMap && window.__menuItemMap[key]) {
      const cachedItem = window.__menuItemMap[key];
      console.log('findMenuItem: found in window.__menuItemMap =', cachedItem);

      if (cachedItem.raw) {
        return normalizeItem(
          cachedItem.raw,
          user?.division,
          isTakeaway,
          user?.UserType,
          !!user?.selectedAdditionalAddress
        );
      }
      
      // Return item with price based on current isTakeaway state
      // Use regular/takeaway prices when available, fallback to legacy cached "price"
      const regularPrice = cachedItem.regularPrice ?? cachedItem.price ?? 0;
      const takeawayPrice = cachedItem.takeawayPrice ?? cachedItem.price ?? regularPrice;
      const price = isTakeaway ? takeawayPrice : regularPrice;
      
      return { ...cachedItem, regularPrice, takeawayPrice, price };
    }

    // 2) fallback to helper that knows about MOCK_MENU structure
    try {
      const fromMock = _findMenuItemHelper(key);
      if (fromMock) {
        console.log('findMenuItem: found in mock =', fromMock);
        return normalizeItem(fromMock, user?.division, isTakeaway, user?.UserType, !!user?.selectedAdditionalAddress);
      }
    } catch (e) {
      // ignore
    }

    // 3) if menuItems is an array (from API), try to find
    if (Array.isArray(menuItems)) {
      const found = menuItems.find(i => String(i.ItemId ?? i.id) === key);
      if (found) {
        console.log('findMenuItem: found in menuItems array =', found);
        return normalizeItem(found, user?.division, isTakeaway, user?.UserType, !!user?.selectedAdditionalAddress);
      }
    }

    // 4) if menuItems is grouped by meal/day (object), try to search deeply
    if (menuItems && typeof menuItems === 'object') {
      for (const k1 of Object.keys(menuItems)) {
        const v1 = menuItems[k1];
        if (Array.isArray(v1)) {
          const found = v1.find(i => String(i.ItemId ?? i.id) === key);
          if (found) {
            console.log('findMenuItem: found in menuItems object array =', found);
            return normalizeItem(found, user?.division, isTakeaway, user?.UserType, !!user?.selectedAdditionalAddress);
          }
        } else if (typeof v1 === 'object') {
          // nested map (e.g. days)
          for (const k2 of Object.keys(v1)) {
            const arr = v1[k2];
            if (!Array.isArray(arr)) continue;
            const found = arr.find(i => String(i.ItemId ?? i.id) === key);
            if (found) {
              console.log('findMenuItem: found in nested menuItems =', found);
              return normalizeItem(found, user?.division, isTakeaway, user?.UserType, !!user?.selectedAdditionalAddress);
            }
          }
        }
      }
    }

    console.log('findMenuItem: item not found for id =', key);
    return null;
  };

  /* ------------------------------ CART / ORDER LOGIC ------------------------------ */
  // addToCart now accepts an optional `itemObj` (the full item returned by the API or mock)
  const addToCart = (dateStr, itemId, delta = 1, itemObj = null) => {
   if (itemObj) {
     const norm = normalizeItem(itemObj, user?.division, isTakeaway, user?.UserType, !!user?.selectedAdditionalAddress);
     if (norm) setMenuItemMap(prev => ({ ...prev, [itemId]: norm }));
   }

  setCart(prev => {
    const currentDateCart = prev[dateStr] || {};
    const currentQty = currentDateCart[itemId] || 0;
    const updatedQty = Math.max(0, currentQty + delta);

    const newDateCart =
      updatedQty > 0
        ? { ...currentDateCart, [itemId]: updatedQty }
        : Object.fromEntries(Object.entries(currentDateCart).filter(([id]) => id !== itemId));

    const newCart = { ...prev, [dateStr]: newDateCart };
    if (Object.keys(newDateCart).length === 0) delete newCart[dateStr];
    return newCart;
  });
};

  const clearCart = () => setCart({});

  const calculateTotals = () => {
    console.log('calculateTotals: cart =', cart);
    let subtotal = 0;
    const dailySubtotals = {};

    for (const dateStr in cart) {
      dailySubtotals[dateStr] = 0;
      for (const itemId in cart[dateStr]) {
        const item = findMenuItem(itemId);
        console.log('calculateTotals: itemId =', itemId, 'item =', item, 'qty =', cart[dateStr][itemId]);
        if (!item) continue;
        const itemTotal = item.price * cart[dateStr][itemId];
        console.log('calculateTotals: itemTotal =', itemTotal);
        subtotal += itemTotal;
        dailySubtotals[dateStr] += itemTotal;
      }
    }

    const deliveryDays = Object.keys(dailySubtotals).filter(d => dailySubtotals[d] > 0).length;
    const total = subtotal;
    console.log('calculateTotals: subtotal =', subtotal, 'total =', total);

    return { subtotal, total, dailySubtotals, deliveryDays };
  };

 const placeOrder = ({ paymentMethod = 'cod', address = '', remarks = '', apiOrderId = null }) => {
  const totals = calculateTotals();
  const now = toLocalISODate();
  
  // Use API order ID if provided, otherwise generate local ID
  const id = apiOrderId ? `ORD${apiOrderId}` : `ORD${Math.floor(Math.random() * 900) + 100}`;

  // Copy cart items into orderDetails
  const orderCart = { ...cart }; // { dateStr: { itemId: qty } }

  const newOrder = {
    id,
    date: now,
    cart: orderCart,       
    totals,               
    paymentMethod,       
    address,              
    remarks,             
    status: 'Processing',
    user,
    isTakeaway: isTakeaway, // Include isTakeaway status in order details
  };

  setOrderDetails(newOrder);
  setOrderHistory(prev => [newOrder, ...(prev || [])]);
  clearCart(); // clear the cart after placing order
};

  // small auth helpers used by app during development
  const loginMock = (userObj) => {
    setUser(userObj);
    setIsAuthenticated(true);
  };
  const registerMock = (userObj) => {
    setUser(userObj);
    setIsAuthenticated(true);
  };
  // const logout = () => {
  //   setUser(null);
  //   setIsAuthenticated(false);
  //   setOrderDetails(null);
  //   // keep orderHistory
  // };

  const clearSessionStorage = () => {
    sessionStorage.clear();
  };

const logout = useCallback(async (options = {}) => {
  const sessionExpiredMessage = options?.sessionExpiredMessage;

  if (!sessionExpiredMessage) {
    await api.CustomerLogout();
  } else {
    api.clearTokens();
  }

  setIsAuthenticated(false);
  setUser(null);
  setCart({});
  setOrderDetails(null);

  sessionStorage.clear();

  if (sessionExpiredMessage) {
    sessionStorage.setItem('sessionExpiredMessage', sessionExpiredMessage);
  }

  [
    'user',
    'tempUser',
    'nextPage',
    'otpSource',
    'forgotMobile',
    'orderDetails',
    'orderHistory',
    'menuData'
  ].forEach((key) => localStorage.removeItem(key));

  document.cookie = 'username=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = 'password=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';

  window.location.href = "/";
}, []);

  useEffect(() => {
    const onSessionExpired = (event) => {
      event.preventDefault();
      logout({ sessionExpiredMessage: event.detail?.message || SESSION_EXPIRED_MESSAGE });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    };
  }, [logout]);
  const value = useMemo(() => ({
    loading, isAuthenticated, user, cart, orderDetails, orderHistory,
    setUser, setIsAuthenticated, addToCart, clearCart, calculateTotals, placeOrder,
    setOrderDetails, loginMock, registerMock, logout, setCart,
    menuItems, setMenuItems, findMenuItem, clearSessionStorage,
    isTakeaway, setIsTakeaway, deliveryMode, setDeliveryMode: setDeliveryModeAndPricing
  }), [loading, isAuthenticated, user, cart, orderDetails, orderHistory, menuItems, menuItemMap, isTakeaway, deliveryMode, logout]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
